import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  PhotoLibrary as PhotoLibraryIcon,
  VideoLibrary as VideoLibraryIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';
import Spacer from '@app/components/atoms/Spacer';
import { useTranslation } from '@app/config/i18n';

const FileInput = ({ 
  onFilesSelected, 
  uploadType, 
  setUploadType, 
  disabled = false,
  buttonProps = {},
  menuProps = {}
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMenuItemClick = useCallback((type) => {
    setAnchorEl(null);
    let acceptType = '';
    
    switch (type) {
      case 'photo':
        acceptType = 'image/*';
        setUploadType('media');
        break;
      case 'video':
        acceptType = 'video/*';
        setUploadType('media');
        break;
      case 'files':
        acceptType = '.txt, .pdf, .doc, .docx, .rtf, .json, .csv, .xls, .xlsx, .xml, .html, .css, .js, .py, .java, .c, .cpp, .sql, .mdb, .accdb, .sqlite, .db, .dbf, .mdb, .sql, .yaml, .yml, .php, .asp, .aspx, .jsp, .sh, .bat, .ps1, .rb, .pl';
        setUploadType('file');
        break;
      default:
        break;
    }

    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('accept', acceptType);
      fileInputRef.current.click();
    }
  }, [setUploadType]);

  const handleFilesSelected = useCallback((event) => {
    const { target } = event;
    const files = Array.from(target.files);
    
    if (files.length > 0 && onFilesSelected) {
      onFilesSelected(files);
    }
    
    // Reset the input value so the same file can be selected again
    target.value = '';
  }, [onFilesSelected]);

  return (
    <>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFilesSelected}
        multiple
      />

      {/* Add button */}
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        {...buttonProps}
      >
        <AddIcon />
      </IconButton>

      {/* File type selection menu */}
      <Menu
        id="file-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        {...menuProps}
      >
        <MenuItem onClick={() => handleMenuItemClick('photo')} disabled={uploadType === 'file'}>
          <PhotoLibraryIcon color='primary' />
          <Spacer width='0.5rem' />
          {t('gallery')}
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('video')} disabled={uploadType === 'file'}>
          <VideoLibraryIcon color='primary' />
          <Spacer width='0.5rem' />
          {t('videos')}
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('files')} disabled={uploadType === 'media'}>
          <FileCopyIcon color='primary' />
          <Spacer width='0.5rem' />
          {t('upload')}
        </MenuItem>
      </Menu>
    </>
  );
};

FileInput.propTypes = {
  onFilesSelected: PropTypes.func.isRequired,
  uploadType: PropTypes.string,
  setUploadType: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  buttonProps: PropTypes.object,
  menuProps: PropTypes.object,
};

FileInput.defaultProps = {
  uploadType: null,
  disabled: false,
  buttonProps: {},
  menuProps: {},
};

export default FileInput;
